//External
import { Injectable } from '@angular/core';
import { Observable, catchError, map, forkJoin, of } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

//Internal
import { Errors, POST, RequestsService } from '../../global/request-service/requests.service';
import { ConfigService } from '../../../config/config.service';
import { ValuesService } from '../../../values/values.service';
import { ProfilesService } from '../../process/profiles/profiles.service';
import { UsefulService } from '../../global/useful/useful.service';
import { SubscriptionsService } from '../../process/subscriptions/subscriptions.service';
import { SubscriptionsValuesService } from '../../../values/subscriptions.values.service';
import { DevicesService } from '../../process/devices/devices.service';
import { ModalName, ModalRoutelessService } from '../../../components/ui/ui-modal-routeless/modal.routeless.service';
import { AllModalInformation, ModalContainerOptions, ModalContentOptions, ModalSize } from '../../global/modal/Modal.model';

@Injectable({
    providedIn: 'root'
  })
export class BucketTestingService {

    constructor(
        readonly requestsService: RequestsService,
        readonly valuesService: ValuesService,
        readonly configService: ConfigService,
        readonly cookieService: CookieService,
        readonly profileService: ProfilesService,
        readonly usefulService: UsefulService,
        readonly subscriptionsService: SubscriptionsService,
        readonly devicesService: DevicesService,
        readonly subscriptionsValuesService: SubscriptionsValuesService,
        private readonly modalRoutelessService: ModalRoutelessService
    ) { }

    /**
     * Makes the request call
     * @param {string} method The name of the method to be called
     * @param {object} params An object containing the params needed for the request
     * @returns {Observable} The response from the server
     */
    private makeRequest(method: string, params?: any): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 1000).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method,
            params: {}
        };

        if (params) {
            Object.assign(json.params, params);
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.bucketTestingService,
            json,
            POST
        ).pipe(
            map(resp => {
                if (resp.error || resp.status === Errors.ERROR) {
                    throw resp.internal_data || resp;
                } else {
                    return resp?.result;
                }
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * Clears the superapp eligibility cache
     */
    public clearSuperappEligibilityCache(): void {
        sessionStorage.removeItem(this.valuesService.sessionStorageSuperappEligible);
    }

    /**
     * Re-runs bucket testing with fingerprint and group_id after family group creation.
     * Bypasses cached eligibility so the backend can associate the group with an existing bucket.
     */
    public refreshSuperappEligibilityWithGroup(groupId: string): Observable<boolean> {
        this.clearSuperappEligibilityCache();
        const superappReq = this.getSettingsSuperappVariant({ group_id: groupId });
        const agentReq = this.getSettingsAgentVariant();

        return forkJoin([superappReq, agentReq]).pipe(
            map(([superappResp, agentResp]) => {
                const isEligible = !!superappResp?.settings?.superapp || !!agentResp?.settings?.superapp;
                if (isEligible) {
                    sessionStorage.setItem(this.valuesService.sessionStorageSuperappEligible, 'true');
                }
                return isEligible;
            }),
            catchError(() => of(false))
        );
    }

    public checkSuperappEligibility(optionalSuperappFields?: any): Observable<boolean> {
        if (this.profileService.hasMspOrXspAnyLevel()) {
            return of(false);
        }

        const sessionStorageKey = this.valuesService.sessionStorageSuperappEligible;
        const cachedEligibility = sessionStorage.getItem(sessionStorageKey);
        
        //extra check for groups 
        if (cachedEligibility !== null) {
            return of(cachedEligibility === 'true');
        }

        const traits = this.profileService.getOwner()?.traits;
        if (traits && (traits.includes('DLP_MIGRATION') || traits.includes('DLP_ENABLED'))) {
            sessionStorage.setItem(sessionStorageKey, 'true');
            return of(true);
        }

        const superappReq = this.getSettingsSuperappVariant(optionalSuperappFields);
        const agentReq = this.getSettingsAgentVariant();

        return forkJoin([superappReq, agentReq]).pipe(
            map(([superappResp, agentResp]) => {
                const isEligible = !!superappResp?.settings?.superapp || !!agentResp?.settings?.superapp;
                if (isEligible) {
                    sessionStorage.setItem(sessionStorageKey, 'true');
                }
                return isEligible;
            }),
            catchError(() => {
                return of(false);
            })
        );
    }

    private getSettingsSuperappVariant(optionalFields?: any): Observable<any> {
        const groupId = this.profileService.getCurrentGroupId();
        const params = {
            protocol_version: 1,
            metadata: {
                app_id: this.valuesService.appSuperapp,
                app_version: '28.0.0.1',
                user_id: this.profileService.getCurrentContextId(),
                current_buckets: [], 
            },
            fields: {
                fingerprint: this.profileService.getOwnerFingerprint(),
                account_created_ts: this.profileService.getOwnerCreatedTs()?.toString(),
                country: this.profileService.getOwnerCountry(),
                ...(groupId && { group_id: groupId }),
                os_type: "all"
            }
        };
        if (this.subscriptionsService.subscriptions.allServices.length === 1 
            && this.subscriptionsService.subscriptions.allServices[0].status === this.subscriptionsValuesService.status.active
            && this.devicesService.devices.all.length === 0) {
            const newFields = {
                bundle_id: this.subscriptionsService.subscriptions.allServices[0].bundle_id, // mandatory if subscription available
                subscription_type: this.subscriptionsService.subscriptions.allServices[0].type, // mandatory if subscription available
                active_slots: this.subscriptionsService.subscriptions.allServices[0].active_slots.toString(), // mandatory if subscription available - always 0 for new bundle
                max_shares: this.subscriptionsService.subscriptions.allServices[0].max_shares.toString(), // mandatory if subscription available
            };
            Object.assign(params.fields, newFields);
        }
        if (optionalFields) {
            Object.assign(params.fields, optionalFields);
        }
        return this.makeRequest('get_settings', params).pipe(catchError(() => of({})));
    }

    private getSettingsAgentVariant(): Observable<any> {
        const params = {
            protocol_version: 1,
            metadata: {
                app_id: this.valuesService.appAgent,
                app_version: '28.0.0.1',
                user_id: this.profileService.getCurrentContextId(),
                current_buckets: [], 
            },
            fields: {
                fingerprint: this.profileService.getOwnerFingerprint()
            }
        };
        return this.makeRequest('get_settings', params).pipe(catchError(() => of({})));
    }

    /**
     * Opens the appropriate install modal based on SuperApp eligibility.
     * - Reads the cached eligibility from sessionStorage first.
     * - If missing, fetches bucket-testing settings with mandatory fields only.
     * - When eligible, opens the SuperApp install modal with default options.
     * - Otherwise opens the classic install modal using provided options.
     * @param containerOptions Modal container options for the classic install modal
     * @param contentOptions Modal content options for the classic install modal
     * @param override Modal override options for the classic install modal
     */
    public openInstallModal(
        containerOptions?: ModalContainerOptions,
        contentOptions?: ModalContentOptions,
        override = false
    ): void {
        const openModal = (isEligible: boolean): void => {
            if (isEligible) {
                const defaultContainer: ModalContainerOptions = { size: ModalSize.LG };
                const defaultContent: any = { isFamilyPlanAdmin: this.profileService.isFamilyPlanAdmin(), 
                    ...contentOptions
                 };
                this.modalRoutelessService.open(ModalName.installSuperappModal, defaultContainer, defaultContent, true);
                return;
            }

            const fallbackContainer = containerOptions ?? { size: ModalSize.LG };
            this.modalRoutelessService.open(ModalName.installModal, fallbackContainer, contentOptions, true);
        };

        this.checkSuperappEligibility().subscribe({
            next: isEligible => {
                openModal(isEligible);
            },
            error: () => {
                openModal(false);
            }
        });
    }

    /**
     * Builds redeem confirmation modal info for the given eligibility result.
     */
    public buildRedeemConfirmationModalInfo(
        contentOptions?: ModalContentOptions,
        containerOptions?: ModalContainerOptions,
        isEligible = false
    ): AllModalInformation {
        return {
            name: isEligible
                ? ModalName.redeemConfirmationSuperappModal
                : ModalName.redeemConfirmationModal,
            containerOptions: {
                ...(containerOptions ?? {}),
                size: containerOptions?.size ?? (isEligible ? ModalSize.LG : ModalSize.MD)
            },
            contentOptions
        };
    }

    /**
     * Resolves which redeem confirmation modal to open based on SuperApp eligibility.
     */
    public getRedeemConfirmationModalInfo(
        contentOptions?: ModalContentOptions,
        containerOptions?: ModalContainerOptions
    ): Observable<AllModalInformation> {
        return this.checkSuperappEligibility().pipe(
            map(isEligible => this.buildRedeemConfirmationModalInfo(contentOptions, containerOptions, isEligible)),
            catchError(() => of(this.buildRedeemConfirmationModalInfo(contentOptions, containerOptions, false)))
        );
    }

    /**
     * Opens the appropriate redeem confirmation modal based on SuperApp eligibility.
     */
    public openRedeemConfirmationModal(
        containerOptions?: ModalContainerOptions,
        contentOptions?: ModalContentOptions,
        override = true
    ): void {
        this.getRedeemConfirmationModalInfo(contentOptions, containerOptions).subscribe({
            next: modalInfo => {
                this.modalRoutelessService.open(
                    modalInfo.name,
                    modalInfo.containerOptions,
                    modalInfo.contentOptions,
                    override
                );
            }
        });
    }

}
